import {
  DEFAULT_BROWSER_VIDEO_MP4_STREAM_MAX_DURATION_SECONDS,
  DEFAULT_BROWSER_VIDEO_MP4_STREAM_MAX_OUTPUT_FRAMES,
  createBrowserVideoMp4FrameEncoder,
  exportBrowserVideoMp4,
  exportBrowserVideoMp4ToSink,
  normalizeBrowserVideoMp4ExportSettings,
  type BrowserVideoMp4ExportSettings,
  type BrowserVideoMp4FrameEncoder,
  type BrowserVideoMp4OutputSink,
  type FrameReaderLike,
  type VideoRenderConfig,
  type VideoRenderDiagnosticEvent,
} from '@sharefe/share3d-engine'
import type { Gs3dClipTemplateDetails } from './gs3dClipTemplateApplier'

export type Gs3dVideoExportFormat = 'mp4'
export type Gs3dVideoExportMode = 'memory' | 'stream'
export type Gs3dVideoExportStage =
  | 'idle'
  | 'runtime'
  | 'stabilize'
  | 'template'
  | 'render'
  | 'encode'
  | 'complete'
  | 'cancelled'
  | 'error'

export interface Gs3dVideoExportSettings extends Required<BrowserVideoMp4ExportSettings> {
  format: Gs3dVideoExportFormat
}

export const GS3D_VIDEO_STREAMING_THRESHOLD_SECONDS = 60
export const GS3D_VIDEO_STREAM_MAX_DURATION_SECONDS =
  DEFAULT_BROWSER_VIDEO_MP4_STREAM_MAX_DURATION_SECONDS
export const GS3D_VIDEO_STREAM_MAX_OUTPUT_FRAMES =
  DEFAULT_BROWSER_VIDEO_MP4_STREAM_MAX_OUTPUT_FRAMES

export interface Gs3dVideoExportProgress {
  stage: Gs3dVideoExportStage
  frameNumber: number
  totalFrames: number
  progress: number
  message: string
}

export interface Gs3dVideoExportResultBase {
  exportMode: Gs3dVideoExportMode
  url?: string
  fileName: string
  mimeType: string
  framesEncoded: number
  durationSeconds: number
  bytes: number
  diagnosticEvents: VideoRenderDiagnosticEvent[]
}

export interface Gs3dMemoryVideoExportResult extends Gs3dVideoExportResultBase {
  exportMode: 'memory'
  blob: Blob
  url: string
}

export interface Gs3dStreamVideoExportResult extends Gs3dVideoExportResultBase {
  exportMode: 'stream'
  fileHandle?: FileSystemFileHandle
}

export type Gs3dVideoExportResult = Gs3dMemoryVideoExportResult | Gs3dStreamVideoExportResult

export type Gs3dVideoFrameEncoder = BrowserVideoMp4FrameEncoder

export interface Gs3dVideoExportOptions {
  container: HTMLElement
  settings: Gs3dVideoExportSettings
  clipTemplate: Gs3dClipTemplateDetails
  scene?: unknown
  frameReader?: FrameReaderLike
  runtimeConfig?: Partial<VideoRenderConfig>
  encoder?: Gs3dVideoFrameEncoder
  exportMode?: Gs3dVideoExportMode
  fileHandle?: FileSystemFileHandle
  streamingSink?: BrowserVideoMp4OutputSink
  fileName?: string
  signal?: AbortSignal
  onProgress?: (progress: Gs3dVideoExportProgress) => void
}

export function normalizeGs3dVideoExportSettings(
  input: Partial<Gs3dVideoExportSettings> = {},
): Gs3dVideoExportSettings {
  const settings = normalizeBrowserVideoMp4ExportSettings(input)
  return {
    ...settings,
    durationSeconds: clampNumber(
      input.durationSeconds,
      0.1,
      GS3D_VIDEO_STREAM_MAX_DURATION_SECONDS,
      4,
    ),
    format: 'mp4',
  }
}

export function createGs3dEffectAnimationClipTemplateDetails(
  details: Gs3dClipTemplateDetails,
): Gs3dClipTemplateDetails {
  return {
    cameraMotionTemplate: details.cameraMotionTemplate,
    ...(details.cameraClip ? { cameraClip: details.cameraClip } : {}),
    ...(details.cameraMotionPlan ? { cameraMotionPlan: details.cameraMotionPlan } : {}),
    effectTemplate: details.effectTemplate,
    durationSeconds: clampNumber(
      details.durationSeconds,
      0.1,
      GS3D_VIDEO_STREAM_MAX_DURATION_SECONDS,
      4,
    ),
    initialCamera: details.initialCamera,
    effectContext: details.effectContext,
  }
}

export function createCanvasFrameReader(canvas: HTMLCanvasElement): FrameReaderLike {
  const frameCanvas = document.createElement('canvas')
  const context = frameCanvas.getContext('2d', { willReadFrequently: true })
  if (!context) {
    throw new Error('Canvas 2D frame readback is unavailable.')
  }

  return {
    async readFrame(request) {
      frameCanvas.width = request.width
      frameCanvas.height = request.height
      context.clearRect(0, 0, request.width, request.height)
      context.drawImage(canvas, 0, 0, request.width, request.height)
      const image = context.getImageData(0, 0, request.width, request.height)
      const data = new Uint8Array(image.data)
      return {
        width: request.width,
        height: request.height,
        pixelRatio: request.pixelRatio,
        frameNumber: request.frameNumber,
        timestampSeconds: request.timestampSeconds,
        data,
        colorSpace: 'srgb',
        alphaMode: 'straight',
        origin: 'top-left',
        yFlipped: false,
        diagnostics: {
          strategy: 'backbuffer-readpixels',
          byteLength: data.byteLength,
          origin: 'top-left',
          colorSpace: 'srgb',
          alphaMode: 'straight',
          yFlipped: false,
        },
      }
    },
  }
}

export function createMp4VideoFrameEncoder(
  settings: Gs3dVideoExportSettings,
): Gs3dVideoFrameEncoder {
  return createBrowserVideoMp4FrameEncoder(settings)
}

export async function pickGs3dVideoExportFileHandle(
  fileName: string,
): Promise<FileSystemFileHandle | undefined> {
  const picker = (
    globalThis as typeof globalThis & {
      showSaveFilePicker?: (options?: {
        suggestedName?: string
        types?: Array<{ description?: string; accept: Record<string, string[]> }>
      }) => Promise<FileSystemFileHandle>
    }
  ).showSaveFilePicker
  if (typeof picker === 'function') {
    return picker({
      suggestedName: fileName,
      types: [
        {
          description: 'MP4 video',
          accept: {
            'video/mp4': ['.mp4'],
          },
        },
      ],
    })
  }

  const getDirectory = navigator.storage?.getDirectory
  if (typeof getDirectory !== 'function') {
    return undefined
  }
  const root = await getDirectory.call(navigator.storage)
  const exportDirectory = await root.getDirectoryHandle('share3d-video-exports', {
    create: true,
  })
  return exportDirectory.getFileHandle(fileName, { create: true })
}

export async function exportGs3dEffectAnimationVideo(
  options: Gs3dVideoExportOptions,
): Promise<Gs3dVideoExportResult> {
  const settings = normalizeGs3dVideoExportSettings(options.settings)
  const totalFrames = Math.max(1, Math.ceil(settings.durationSeconds * settings.fps))
  const exportMode =
    options.exportMode ??
    (settings.durationSeconds > GS3D_VIDEO_STREAMING_THRESHOLD_SECONDS ? 'stream' : 'memory')
  let objectUrl: string | null = null

  const progress = (
    stage: Gs3dVideoExportStage,
    frameNumber: number,
    message: string,
  ) => {
    options.onProgress?.({
      stage,
      frameNumber,
      totalFrames,
      progress: totalFrames <= 0 ? 0 : frameNumber / totalFrames,
      message,
    })
  }

  const runExport = async (): Promise<Gs3dVideoExportResult> => {
    try {
      const fileBaseName = options.fileName ?? createDefaultVideoExportFileName('mp4')
      if (exportMode === 'stream') {
        const sink =
          options.streamingSink ??
          (options.fileHandle
            ? await createFileSystemAccessSink(options.fileHandle)
            : undefined)
        if (!sink) {
          throw new Error('超长视频流式导出需要浏览器支持 File System Access API。')
        }
        const exported = await exportBrowserVideoMp4ToSink({
          container: options.container,
          settings,
          scene: options.scene ?? { kind: 'gs3d-effect-animation-current-editor' },
          timelineTemplate: options.clipTemplate,
          runtimeConfig: {
            frameReader: options.frameReader,
            ...options.runtimeConfig,
          },
          sink,
          maxDurationSeconds: GS3D_VIDEO_STREAM_MAX_DURATION_SECONDS,
          maxOutputFrames: GS3D_VIDEO_STREAM_MAX_OUTPUT_FRAMES,
          signal: options.signal,
          onProgress: (event) => {
            progress(event.stage, event.frameNumber, event.message)
          },
        })
        const result: Gs3dStreamVideoExportResult = {
          exportMode: 'stream',
          ...(options.fileHandle ? { fileHandle: options.fileHandle } : {}),
          ...(await createVideoExportDownloadUrl(options.fileHandle)),
          fileName: fileBaseName,
          mimeType: exported.mimeType,
          framesEncoded: exported.framesEncoded,
          durationSeconds: exported.durationSeconds,
          bytes: exported.byteLength,
          diagnosticEvents: exported.diagnosticEvents,
        }
        progress('complete', totalFrames, 'Streaming video export complete.')
        return result
      }
      const exported = await exportBrowserVideoMp4({
        container: options.container,
        settings,
        scene: options.scene ?? { kind: 'gs3d-effect-animation-current-editor' },
        timelineTemplate: options.clipTemplate,
        runtimeConfig: {
          frameReader: options.frameReader,
          ...options.runtimeConfig,
        },
        encoder: options.encoder,
        signal: options.signal,
        onProgress: (event) => {
          progress(event.stage, event.frameNumber, event.message)
        },
      })
      const blob = exported.blob
      objectUrl = URL.createObjectURL(blob)
      const result: Gs3dVideoExportResult = {
        exportMode: 'memory',
        blob,
        url: objectUrl,
        fileName: fileBaseName,
        mimeType: exported.mimeType,
        framesEncoded: exported.framesEncoded,
        durationSeconds: exported.durationSeconds,
        bytes: exported.byteLength,
        diagnosticEvents: exported.diagnosticEvents,
      }
      progress('complete', totalFrames, 'Video export complete.')
      return result
    } catch (error) {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
      throw error
    }
  }

  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request('share3d-gs3d-video-export', { signal: options.signal }, runExport)
  }

  return runExport()
}

export function revokeGs3dVideoExportResult(result: Gs3dVideoExportResult | null): void {
  if (result?.url) {
    URL.revokeObjectURL(result.url)
  }
}

async function createVideoExportDownloadUrl(
  fileHandle: FileSystemFileHandle | undefined,
): Promise<{ url?: string }> {
  if (!fileHandle) {
    return {}
  }
  try {
    const file = await fileHandle.getFile()
    return { url: URL.createObjectURL(file) }
  } catch {
    return {}
  }
}

async function createFileSystemAccessSink(
  fileHandle: FileSystemFileHandle,
): Promise<BrowserVideoMp4OutputSink> {
  const writable = await fileHandle.createWritable()
  let closed = false
  return {
    async write(chunk, position) {
      if (closed) {
        throw new Error('Video export file stream is already closed.')
      }
      await writable.write({ type: 'write', position, data: chunk })
    },
    async close() {
      if (closed) return
      closed = true
      await writable.close()
    },
    async abort(reason) {
      if (closed) return
      closed = true
      await writable.abort(reason)
    },
  }
}

function createDefaultVideoExportFileName(extension: string): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `gs3d-effect-animation-${timestamp}.${extension}`
}

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.min(max, Math.max(min, value))
    : fallback
}
